// ============================================================
// useClientAutoSave.ts
// Phase 4. Persists edits to the linked Client on the current case
// to the backend via PATCH /api/clients/:id. Mirrors useAutoSave's
// singleton pattern (one current client at a time, one debounce
// timer, one in-flight slot, one retry timer) so flushClientAutoSave
// can be a plain function the nav handlers import.
//
// Public API:
//   useClientAutoSave()        — mounted once in App.tsx alongside
//                                useAutoSave. Subscribes to
//                                currentCase.client and schedules a
//                                1s-debounced save on every change.
//   flushClientAutoSave()      — fires any pending edit immediately
//                                and awaits the in-flight save plus
//                                any follow-up triggered by a
//                                keystroke that landed during the
//                                request. Called from ViewingPane's
//                                nav-away handlers.
//   retryClientAutoSave()      — manual retry for the most recent
//                                failure. SaveIndicator-style "Try
//                                again" can be wired up later — kept
//                                exported for parity with useAutoSave.
//
// Errors mirror into the existing useCasevoStore.saveError slot so
// the SaveIndicator UX surfaces client save failures the same way it
// already surfaces case save failures. This deliberately shares one
// slot — a save failure is a save failure as far as the user cares,
// and the indicator's retry button can be wired to either retry
// helper in a follow-up if we want to distinguish.
// ============================================================
import { useEffect } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { Client, UpdateClientPayload } from '../types/client.types'
import { getClientBaseUpdatedAt, setClientBaseUpdatedAt } from './saveBaseVersion'
import { getClientByIdApi } from '../api/casevoApi'
import { serverChangedTouchedColumns, MAX_409_REAPPLY_ATTEMPTS } from './reconcile409'
import { clientScope, refusedField, useFieldRefusalStore } from './fieldRefusals'
import { holdInvalidContact } from '../utils/contactFormat'

// Build the PATCH payload — every user-editable field on Client.
// We send every field every time (rather than diffing) because
// UpdateClientPayload allows partial and a full re-send is simpler
// to reason about than tracking which keys changed. Network cost
// is negligible (a single small JSON object per second of typing).
//
// Phase 4.5 expanded this list with 14 GP/Hospital fields. Those
// are optional on Client (`field?: string`), so undefined values
// drop out of the JSON via JSON.stringify and Prisma's update()
// only touches keys that are present — no risk of zeroing a column
// just because the user hasn't filled it in yet.
const buildClientPayload = (c: Client): UpdateClientPayload => ({
  title: c.title,
  forename: c.forename,
  surname: c.surname,
  addressLine1: c.addressLine1,
  addressLine2: c.addressLine2,
  addressTown: c.addressTown,
  addressCounty: c.addressCounty,
  addressPostcode: c.addressPostcode,
  dateOfBirth: c.dateOfBirth,
  hasNiNumber: c.hasNiNumber,
  niNumber: c.niNumber,
  mainContactNumber: c.mainContactNumber,
  emailAddress: c.emailAddress,
  hasEmailAddress: c.hasEmailAddress ?? null,
  employmentStatus: c.employmentStatus,
  occupation: c.occupation,
  gpName: c.gpName,
  gpAddressLine1: c.gpAddressLine1,
  gpAddressLine2: c.gpAddressLine2,
  gpAddressTown: c.gpAddressTown,
  gpAddressCounty: c.gpAddressCounty,
  gpAddressPostcode: c.gpAddressPostcode,
  gpPhone: c.gpPhone,
  hospitalName: c.hospitalName,
  hospitalAddressLine1: c.hospitalAddressLine1,
  hospitalAddressLine2: c.hospitalAddressLine2,
  hospitalAddressTown: c.hospitalAddressTown,
  hospitalAddressCounty: c.hospitalAddressCounty,
  hospitalAddressPostcode: c.hospitalAddressPostcode,
  hospitalPhone: c.hospitalPhone,
})

// Stage 1a: the exact set of Client fields this hook persists — derived
// from buildClientPayload so it can never drift from what actually saves.
// (buildClientPayload always assigns all keys, even when values are
// undefined, so Object.keys on a throwaway gives the full field list.)
// The edit-sync layer consults this to IGNORE fieldKeys that aren't part
// of the client save path — notably `client.notes` (saved by the separate
// notes autosave) and any non-data keys like address-search boxes — so
// live sync never reaches a satellite save path.
const CLIENT_SYNC_FIELDS = new Set(Object.keys(buildClientPayload({} as Client)))
export const isClientSyncField = (field: string): boolean => CLIENT_SYNC_FIELDS.has(field)

// Module-scope state. Same singleton pattern as useAutoSave —
// guaranteed safe because useClientAutoSave is mounted exactly once
// (in App.tsx) and only one currentCase.client is being edited at
// a time. flush() being a plain function means nav handlers don't
// need a React ref.
//
// pendingTimerId  — active 1s debounce timer (null when nothing pending)
// pendingPayload  — what we'd save when the timer fires (latest snapshot)
// inFlightPromise — currently-running save, if any (null when idle)
// lastSavedRaw    — last successfully-sent serialised client state
// lastError       — most recent save failure, mirrored to the store
//                   so SaveIndicator can render it. Cleared on success.
// retryTimerId    — pending one-shot 5s retry timer (null when none).
//                   Cancelled when a fresher keystroke arrives (the
//                   normal debounce handles it) and when a save
//                   succeeds.
type PendingPayload = {
  id: string
  payload: UpdateClientPayload
  serialised: string
}

let pendingTimerId: ReturnType<typeof setTimeout> | null = null
let pendingPayload: PendingPayload | null = null
let inFlightPromise: Promise<void> | null = null
let lastSavedRaw: string | null = null
let lastError: Error | null = null
let retryTimerId: ReturnType<typeof setTimeout> | null = null
// PARTIAL-SAVE DIFF BASELINE (mirrors useAutoSave for the case).
// lastSavedFlat  — the buildClientPayload() result at the last SUCCESSFUL save
//                  (or the freshly-loaded client, seeded on load). runSave diffs
//                  the current payload against this and sends ONLY changed keys,
//                  so concurrent edits to DIFFERENT client fields don't clobber.
// lastSeededClientId — the client id the baseline was seeded for; a different
//                  client loading re-seeds (loading is not an edit).
let lastSavedFlat: Record<string, unknown> | null = null
let lastSeededClientId: string | null = null

// Return only the keys of `current` whose value CHANGED vs `base`. Value-
// INEQUALITY (v !== base[k]), NOT truthiness — so a field cleared to '' or null
// is INCLUDED (differs from its old value). No baseline yet → full record.
const diffFlat = (
  current: Record<string, unknown>,
  base: Record<string, unknown> | null,
): Record<string, unknown> => {
  if (base === null) return { ...current }
  const changed: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(current)) {
    if (v !== base[k]) changed[k] = v
  }
  return changed
}

const RETRY_DELAY_MS = 5000

// Mirror lastError into the store so SaveIndicator subscribers re-render.
// Outside React, so use the imperative getState() escape hatch.
const publishError = (err: Error | null): void => {
  lastError = err
  useCasevoStore.getState().setSaveError(err)
}

const runSave = (
  payload: PendingPayload,
  suppressRetry: boolean = false,
  // Column-aware 409 recovery attempt counter (0 = first try).
  attempt: number = 0,
): Promise<void> => {
  // Placeholder so the IIFE's `finally` can refer to `promise` — by the
  // time `finally` actually runs, the reassignment below has happened.
  let promise: Promise<void> = Promise.resolve()
  promise = (async () => {
    try {
      // Go through the store's updateClient action rather than calling
      // updateClientApi directly so the firm-wide `clients[]` array
      // stays in sync with the server's response (e.g. an updated
      // updatedAt). The working copy at currentCase.client stays
      // optimistic — it's whatever the user has typed.
      // Send ONLY the client fields that changed since the last save (id is
      // carried by the URL, never part of the diff body). Nothing changed at
      // the field level → skip the call but settle as success below.
      const currentFlat = payload.payload as unknown as Record<string, unknown>
      const changed = diffFlat(currentFlat, lastSavedFlat)
      // A half-typed email / phone isn't sent yet (utils/contactFormat).
      const baseBefore = lastSavedFlat
      const held = holdInvalidContact(changed, currentFlat, { email: 'emailAddress', phone: 'mainContactNumber', hasEmail: 'hasEmailAddress' })
      // A field being sent again drops its old refusal note (hooks/fieldRefusals).
      for (const k of Object.keys(changed)) useFieldRefusalStore.getState().clearRefusal(clientScope(payload.id), k)
      if (Object.keys(changed).length > 0) {
        // Optimistic-concurrency: attach the SERVER updatedAt loaded for this
        // client so the backend can 409 on a stale write. Opt-in — omit when
        // no base was captured.
        const base = getClientBaseUpdatedAt(payload.id)
        const body = (base !== null
          ? { ...changed, baseUpdatedAt: base }
          : changed) as UpdateClientPayload & { baseUpdatedAt?: string }
        const updated = await useCasevoStore.getState().updateClient(payload.id, body)
        // Advance the concurrency baseline to the server's new updatedAt.
        setClientBaseUpdatedAt(payload.id, updated.updatedAt, 'fields-success')
      }
      // else: fields effect fired but the diff is empty (e.g. only notes
      // changed, which buildClientPayload excludes) → no PATCH, settle below.
      lastSavedRaw = payload.serialised
      // Advance the diff baseline to the full just-saved snapshot (a held-back
      // value stays at its saved value, so it's sent once it's valid).
      lastSavedFlat = held.length
        ? { ...currentFlat, ...Object.fromEntries(held.map((k) => [k, (baseBefore ?? {})[k]])) }
        : currentFlat
      // Only clear pendingPayload if it's still the same one we just
      // saved; a newer keystroke during the request may have replaced
      // it already.
      if (pendingPayload && pendingPayload.serialised === payload.serialised) {
        pendingPayload = null
      }
      // Success — clear any prior error and cancel any pending retry.
      if (lastError !== null) publishError(null)
      if (retryTimerId !== null) {
        clearTimeout(retryTimerId)
        retryTimerId = null
      }
    } catch (err) {
      // 409 = optimistic-concurrency conflict (someone else saved this client
      // first). Non-destructive, mirroring useAutoSave: advance the baseline
      // from the 409 body, cancel any retry, drop the stale pending payload
      // (so it can't silently resubmit), and warn — WITHOUT wiping the user's
      // in-progress edit. A later keystroke re-stages against the fresh base.
      const resp = (err as { response?: { status?: number; data?: { currentUpdatedAt?: string } } }).response
      if (resp?.status === 409) {
        if (retryTimerId !== null) { clearTimeout(retryTimerId); retryTimerId = null }
        // COLUMN-AWARE recovery (see reconcile409): re-fetch the client and, if
        // the server didn't change any of the fields THIS save touched (vs our
        // base = lastSavedFlat), silently re-apply on the fresh base — no toast.
        const currentFlat = payload.payload as unknown as Record<string, unknown>
        const touched = Object.keys(diffFlat(currentFlat, lastSavedFlat))
        if (attempt < MAX_409_REAPPLY_ATTEMPTS && touched.length > 0) {
          try {
            const server = await getClientByIdApi(payload.id)
            const serverFlat = buildClientPayload(server) as unknown as Record<string, unknown>
            const overlap = serverChangedTouchedColumns(touched, lastSavedFlat ?? {}, serverFlat)
            if (!overlap) {
              setClientBaseUpdatedAt(payload.id, server.updatedAt, 'fields-409-reapply')
              await runSave(payload, true, attempt + 1)
              return
            }
          } catch { /* re-fetch failed — fall through to the conflict path */ }
        }
        // Genuine same-field conflict, retry cap hit, or re-fetch failed.
        if (resp.data?.currentUpdatedAt) setClientBaseUpdatedAt(payload.id, resp.data.currentUpdatedAt, 'fields-409-giveup')
        if (pendingPayload && pendingPayload.serialised === payload.serialised) {
          pendingPayload = null
        }
        if (lastError !== null) publishError(null)
        useCasevoStore.getState().setSaveConflict(
          "This client was updated by someone else. Your latest change wasn't saved — please review and re-enter it.",
        )
        return
      }
      // ★ 400 naming ONE field (e.g. a date of birth after one of the
      // client's accidents): that field goes back to its SAVED value, the
      // server's message shows under it, and the other changes still save —
      // mirrors useAutoSave.
      const refused = refusedField(err)
      const sentFlat = payload.payload as unknown as Record<string, unknown>
      if (refused && refused.field in sentFlat) {
        if (retryTimerId !== null) { clearTimeout(retryTimerId); retryTimerId = null }
        useFieldRefusalStore.getState().setRefusal(clientScope(payload.id), refused.field, refused.message)
        if (lastError !== null) publishError(null)
        const savedValue = (lastSavedFlat ?? {})[refused.field]
        const store = useCasevoStore.getState()
        if (store.selectedClient && store.selectedClient.id === payload.id) {
          // Show the saved value again; the effect re-stages whatever else
          // changed (the refused field now matches its baseline).
          if (pendingPayload && pendingPayload.serialised === payload.serialised) pendingPayload = null
          store.updateSelectedClient({ [refused.field]: savedValue } as Partial<Client>)
          return
        }
        // Client no longer on screen → send the rest now with the refused
        // field at its saved value.
        const rest = { ...sentFlat, [refused.field]: savedValue }
        if (Object.keys(diffFlat(rest, lastSavedFlat)).length > 0) {
          await runSave({ ...payload, payload: rest as unknown as UpdateClientPayload }, true, attempt)
        }
        return
      }
      const error = err instanceof Error ? err : new Error('Client auto-save failed')
      publishError(error)
      // eslint-disable-next-line no-console
      console.error('[ClientAutoSave] Failed to save client:', err)
      // Schedule one retry — unless we're already in a retry, or the
      // caller (flush) explicitly suppressed it.
      if (!suppressRetry) {
        if (retryTimerId !== null) clearTimeout(retryTimerId)
        retryTimerId = setTimeout(() => {
          retryTimerId = null
          // Use the LATEST pendingPayload — if the user typed since,
          // we save the fresher data instead of the original. If
          // pendingPayload is null, an in-between flush already saved
          // and we have nothing to retry.
          if (pendingPayload) runSave(pendingPayload, true)
        }, RETRY_DELAY_MS)
      }
    } finally {
      // Only clear the in-flight slot if WE were the in-flight promise;
      // a concurrent flush may have started another save by now.
      if (inFlightPromise === promise) inFlightPromise = null
    }
  })()
  inFlightPromise = promise
  return promise
}

// Public: manual retry of the last failed client save. Kept for
// parity with retryAutoSave so a future SaveIndicator extension can
// distinguish case vs client retries.
export const retryClientAutoSave = async (): Promise<void> => {
  if (lastError === null) return
  if (retryTimerId !== null) {
    clearTimeout(retryTimerId)
    retryTimerId = null
  }
  if (inFlightPromise) {
    try { await inFlightPromise } catch { /* logged in runSave */ }
  }
  if (lastError === null || pendingPayload === null) return
  await runSave(pendingPayload, true)
}

// Public: force any pending client edit to persist now. Call before
// navigating away from a case detail screen so the user doesn't lose
// a sub-second debounce window. Safe to call when nothing's pending —
// resolves immediately.
export const flushClientAutoSave = async (): Promise<void> => {
  if (pendingTimerId !== null) {
    clearTimeout(pendingTimerId)
    pendingTimerId = null
  }
  if (inFlightPromise) {
    try { await inFlightPromise } catch { /* logged in runSave */ }
  }
  if (pendingPayload) {
    try { await runSave(pendingPayload, true) } catch { /* logged in runSave */ }
  }
}

// ── Stage 1a: apply a REMOTE client edit without a save-loop ──────────
// Mirror of applyRemoteCaseEdit (see useAutoSave.ts) for the live-client
// surface (ClientSearchView's selectedClient). Applies the remote value via
// updateSelectedClient, then IMMEDIATELY advances this hook's module-scope
// baselines (lastSavedRaw + lastSavedFlat) to the post-apply state so the
// effect below stages ZERO saves — neither for the remote value now, nor for
// the user's next local edit (which diffs against the advanced baseline).
//
// Also advances the client's optimistic-concurrency baseline to the SERVER
// updatedAt the remote save produced (from the message), so this client's
// next PATCH won't 409 against the remote write. Never reads a locally-held
// updatedAt.
//
// No-ops unless the edit targets the client currently loaded in selectedClient
// (guards a late message after the user switched clients).
export const applyRemoteClientEdit = (
  clientId: string,
  updates: Partial<Client>,
  serverUpdatedAt: string,
): void => {
  const store = useCasevoStore.getState()
  const open = store.selectedClient
  if (!open || open.id !== clientId) return
  store.updateSelectedClient(updates)
  const applied = useCasevoStore.getState().selectedClient
  if (!applied) return
  lastSavedRaw = JSON.stringify(applied)
  lastSavedFlat = buildClientPayload(applied) as unknown as Record<string, unknown>
  // Keep the seed id aligned so the effect's client-change branch doesn't
  // re-seed (same client) and swallow the user's next real edit.
  lastSeededClientId = applied.id
  setClientBaseUpdatedAt(applied.id, serverUpdatedAt, 'fields-remote-apply')
}

export const useClientAutoSave = (): void => {
  // Stage 2b: reverted to watching selectedClient ONLY. The case-page
  // Client Details editor now writes to the case-row SNAPSHOT
  // (currentCase.clientDetails) via the case-save path (useAutoSave →
  // PUT /api/cases/:id), so currentCase.client is no longer the
  // mutation target there. This hook is now a single-purpose ClientSearch
  // hook — it serves selectedClient edits, persisted via PATCH
  // /api/clients/:id. The Stage 2a fallback that also watched
  // currentCase.client was a temporary bridge while the case page still
  // mutated the live Client; Stage 2b's untangle makes it unnecessary.
  const client = useCasevoStore((state) => state.selectedClient ?? null)

  // beforeunload guard — mirrors useAutoSave so a hard navigate or
  // tab close mid-typing surfaces the browser's "Changes you may
  // not have saved" dialog whenever there's a pending or in-flight
  // client edit. In-app navigation goes through flushClientAutoSave
  // instead, which clears both before navigating.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (pendingPayload === null && inFlightPromise === null) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  useEffect(() => {
    if (!client) return

    const serialised = JSON.stringify(client)

    // Seed the diff baseline when a DIFFERENT client loads (loading isn't an
    // edit) so the first real edit diffs against the loaded values, not a
    // previous client's snapshot or a stale lastSavedRaw.
    if (client.id !== lastSeededClientId) {
      lastSeededClientId = client.id
      lastSavedRaw = serialised
      lastSavedFlat = buildClientPayload(client) as unknown as Record<string, unknown>
      return
    }

    if (serialised === lastSavedRaw) return

    pendingPayload = {
      id: client.id,
      payload: buildClientPayload(client),
      serialised,
    }

    // A new keystroke supersedes any pending retry — the regular
    // debounce will pick up this fresher data shortly anyway.
    if (retryTimerId !== null) {
      clearTimeout(retryTimerId)
      retryTimerId = null
    }

    if (pendingTimerId !== null) clearTimeout(pendingTimerId)
    pendingTimerId = setTimeout(() => {
      pendingTimerId = null
      if (pendingPayload) runSave(pendingPayload)
    }, 1000)

    // On unmount or before the next effect run, only clear the timer —
    // do NOT discard pendingPayload, because flushClientAutoSave may
    // still need it (nav handler calls flush then immediately clears
    // the current case, which re-runs this effect with no client).
    return () => {
      if (pendingTimerId !== null) {
        clearTimeout(pendingTimerId)
        pendingTimerId = null
      }
    }
  }, [client])
}
